import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Ripple } from "primereact/ripple";
import { confirmDialog } from 'primereact/confirmdialog';
import { Toast } from 'primereact/toast';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from "primereact/button";

import CompletionCertificateModal from "./CompletionCertificateModal";
import DocumentViewModal from "../../../../../Components/DocumentViewModal";
import NoDataCreated from "../../../../../Components/NoDataCreated";
import ErrorPage from "../../../../../Components/ErrorPage";
import PageLoader from "../../../../../Components/PageLoader";
import { goToTop } from "../../../../../Components/goToTop";

import { CompletionCertificates } from "../../../../../Controllers/Types/Project/DefaultService/CompletionCertificateInterface";
import { completionCertificateDeleteAPI, completionCertificateGetDataApi } from "../../../../../Api/Project/DefaultServices/CompletionCertificateApi";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../Controllers/Store";
import { hasPermission } from "../../../../../Controllers/Selectors/permissionsSelector";
import TableActionLoader from "../../../../../Components/TableActionLoader";
interface Status {
    name: string;
    colorCode: string;
}

const CompletionCertificate: React.FC = () => {
    const navigate = useNavigate();
    const toast = useRef<Toast>(null);
    const location = useLocation();
    const projectData = location.state?.data;
    const serviceId = Number(projectData.folder_id);
    const [dataLoading, setDataLoading] = useState<boolean>(false);
    const [initialDataloading, setInitialDataLoading] = useState<boolean>(true);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    // const isShared: boolean = projectData.sharedBy !== null ? true : false;
    const isShared: boolean = projectData.can_edit === false ? true : false;

    const [deleteLoadingId, setdeleteLoadingId] = useState<number | null>(null);

    const statuses: Status[] = [
        {
            name: "Received",
            colorCode: '#B9F6CA'
        },
        {
            name: "In Progress",
            colorCode: '#FFEFBC'
        },
        {
            name: "Pending",
            colorCode: '#EF9A9A'
        }
    ];

    const [dataState, setDataState] = useState<'Add' | 'Edit'>('Add');

    const [showFileViewModal, setShowFileViewModal] = useState(false);
    const [fileUrl, setFileUrl] = useState<string | null>(null);

    const [completionCertificateData, setCompletionCertificateData] = useState<CompletionCertificates[]>([]);
    const [showDetailModal, setShowDetailModal] = useState<boolean>(false);
    const [selectedComCerData, setSelectedComCerData] = useState<CompletionCertificates | null>(null);

    const useCanEdit = (screen: string, permission: string) => {
        return useSelector((state: RootState) => hasPermission(state, screen, permission));
    };

    const isPermission_add_completion_certificate = useCanEdit('Completion Certificate', 'add_completion_certificate');
    const isPermission_edit_completion_certificate = useCanEdit('Completion Certificate', 'edit_completion_certificate');
    const isPermission_delete_completion_certificate = useCanEdit('Completion Certificate', 'delete_completion_certificate');
    const isPermission_change_progress = useCanEdit('Completion Certificate', 'change_progress');


    useEffect(() => {
        completionCertificateDataGet();
    }, [serviceId]);

    /* ********************************* get data list ************************************************** */
    const completionCertificateDataGet = async () => {
        if (serviceId) {
            setDataLoading(true);
            const data = await completionCertificateGetDataApi(Number(serviceId));
            setDataLoading(false);
            setInitialDataLoading(false);

            if (data.status) {
                setCompletionCertificateData(data.data || []);
            } else {
                setErrorMessage(`An error occurred while retrieving ${(projectData.feature_name).toLowerCase()} data.`);
            }
        }
    }
    /* ********************************* get data list ************************************************** */


    /* ************************** For completion certificate ************************ */
    const handleAddDetails = () => {
        setShowDetailModal(true);
        setDataState('Add');
    }

    const handleEditDetails = (comCerData: CompletionCertificates) => {
        setShowDetailModal(true);
        setDataState('Edit');
        setSelectedComCerData(comCerData);
    }

    /* *************************************************  Delete ***************************************** */
    const handleDeleteData = (comCerId: number): void => {
        confirmDialog({
            message: 'Are you sure you want to delete this certificate data?',
            header: 'Confirm the deletion',
            icon: 'bi bi-info-circle',
            defaultFocus: 'accept',
            acceptClassName: 'p-button-danger',
            accept: () => deleteData(comCerId),
            dismissableMask: true,
            resizable: false,
            draggable: false,
        });
    }

    const deleteData = async (comCerId: number) => {
        setdeleteLoadingId(comCerId);
        const data = await completionCertificateDeleteAPI(comCerId);

        setdeleteLoadingId(null);
        if (data.statusCode === 200) {
            completionCertificateDataGet();

            if (toast.current) {
                toast.current?.show({
                    severity: "success",
                    summary: "Success",
                    detail: `Certificate data deleted successfully.`,
                    life: 3000,
                });
            }
        } else {
            if (toast.current) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Failed',
                    detail: 'Error deleting document data.',
                    life: 3000
                });
            }
        }
    }
    /* *************************************************  Delete ***************************************** */

    const handleCloseDetailModal = () => {
        setShowDetailModal(false);
    }
    /* *************************************************************************** */


    /* ***************************** For table view ****************************** */
    const getRowClassName = (data: CompletionCertificates, options: { [key: string]: any }) => {
        if (data.status === 'Received') {
            return 'table_data_row success';
        } else if (data.status === 'In Progress') {
            return 'table_data_row warning';
        } else if (data.status === 'Pending') {
            return 'table_data_row danger';
        }
    };

    /* Table structure */
    const editDataBody = (rowData: CompletionCertificates) => {
        const data = rowData;

        return (
            <button className="data_action_btn primary_table_btn p-ripple"
                onClick={() => handleEditDetails(data)}
            >
                <i className="bi bi-pencil-square"></i>
                <Ripple />
            </button>
        )
    }

    const deleteDataBody = (rowData: CompletionCertificates) => {
        const dataId = rowData.id;

        return (
            <React.Fragment>
                {deleteLoadingId === dataId ? (
                    <TableActionLoader />
                ) : (
                    <button className="data_action_btn danger_table_btn p-ripple"
                        onClick={() => handleDeleteData(dataId)}
                    >
                        <i className="bi bi-trash3"></i>
                        <Ripple />
                    </button>
                )}
            </React.Fragment>
        )
    }

    const viewDocumentBody = (rowData: CompletionCertificates) => {
        const filePath = rowData.path;

        return (
            <div className="tabel_btn_area justify-content-end justify-content-lg-center mt-1 mb-1">
                <Button
                    label="View"
                    className="tabel_btn view_btn"
                    disabled={!filePath}
                    onClick={() => handleViewDocument(filePath ?? '')}
                />
            </div>
        )
    };
    /* *************************************************************************** */


    /* ***************************** For view file ****************************** */
    const handleViewDocument = (document: string) => {
        setFileUrl(document);
        setShowFileViewModal(true);
    }

    const handleCloseDocumentViewModal = () => {
        setShowFileViewModal(false);
        setFileUrl('');
    };
    /* *************************************************************************** */


    /* **************************** Common functions ***************************** */
    const goBack = () => {
        navigate(-1);
        goToTop();
    };
    /* *************************************************************************** */


    return (
        <React.Fragment>
            <Toast ref={toast} />

            <div className="main_content layout_padding show_side_bar">
                {dataLoading && initialDataloading ? (
                    <PageLoader />
                ) : errorMessage ? (
                    <ErrorPage errorMessage={errorMessage} />
                ) : (
                    <div className="page_content mt-0">
                        <div className="page_breadcrumb_area">
                            <ul className="page_breadcrumb">
                                <li className="page_breadcrumb_item has_child has_link" onClick={() => navigate(-1)}>
                                    {projectData.job_number} : {projectData.address}
                                </li>
                                <li className="page_breadcrumb_item has_child">
                                    {projectData.folder}
                                </li>
                                <li className="page_breadcrumb_item active">
                                    {projectData.feature_name}
                                </li>
                            </ul>

                            <button className="back_btn p-ripple" onClick={goBack}>
                                <i className="bi bi-arrow-left me-2"></i>
                                Back
                                <Ripple />
                            </button>
                        </div>

                        <div className="page_header_area mt-sm-3 mt-3">
                            <div className="status_lable_container">
                                {completionCertificateData?.length > 0 && (
                                    <div className="status_lable_area">
                                        {statuses?.map((status, index) => (
                                            <div key={index} className="status_lable">
                                                <span style={{ backgroundColor: status.colorCode }}></span>
                                                <h6>{status.name}</h6>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="page_action_area">
                                {isPermission_add_completion_certificate && !isShared && (
                                    <button className="add_data_link_btn p-ripple" onClick={handleAddDetails}>
                                        <i className="bi bi-plus-circle"></i>
                                        <span>Add certificate</span>
                                        <Ripple />
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="page_main_content_section">
                            {completionCertificateData?.length > 0 ? (
                                <DataTable
                                    value={completionCertificateData}
                                    paginator
                                    size="small"
                                    rows={10}
                                    rowsPerPageOptions={[5, 10, 25, 50]}
                                    tableStyle={{ minWidth: '50rem' }}
                                    paginatorDropdownAppendTo={'self'}
                                    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                                    currentPageReportTemplate="{first} to {last} of {totalRecords}"
                                    className="page_table p-0 p-sm-2"
                                    rowHover
                                    rowClassName={getRowClassName}
                                    scrollable
                                    loading={dataLoading}
                                    dataKey="id"
                                >
                                    <Column
                                        header="Document name"
                                        headerClassName="text_purple"
                                        field="certificate_title"
                                        body={(rowData: CompletionCertificates, { rowIndex }) => (
                                            <span>
                                                {rowData.certificate_title ? (rowIndex + 1) + '. ' + rowData.certificate_title : '---'}
                                            </span>
                                        )}
                                        style={{ width: '40%' }}
                                    ></Column>

                                    <Column
                                        header="Date"
                                        headerClassName="text_purple"
                                        alignHeader={'center'}
                                        field="applied_date"
                                        body={(rowData: CompletionCertificates) => (
                                            <span className="text_no_wrap flex_center">
                                                {rowData.applied_date ? rowData.applied_date : '---'}
                                            </span>
                                        )}
                                        style={{ width: '20%' }}
                                    ></Column>

                                    {isPermission_edit_completion_certificate && !isShared && (
                                        <Column
                                            header="Edit"
                                            headerClassName="text_purple"
                                            alignHeader="center"
                                            body={editDataBody}
                                            className="data_action_btn_area"
                                            style={{ width: '10%' }}
                                        ></Column>
                                    )}

                                    {isPermission_delete_completion_certificate && !isShared && (
                                        <Column
                                            header="Delete"
                                            headerClassName="text_purple"
                                            alignHeader="center"
                                            body={deleteDataBody}
                                            className="data_action_btn_area"
                                            style={{ width: '10%' }}
                                        ></Column>
                                    )}

                                    <Column
                                        body={viewDocumentBody}
                                        className="table_btn_area"
                                        style={{ width: '20%' }}
                                        frozen
                                        alignFrozen="right"
                                    ></Column>
                                </DataTable>
                            ) : (
                                <NoDataCreated
                                    message={`No ${(projectData.feature_name).toLowerCase()} document added. ${!isShared && isPermission_add_completion_certificate ? 'To add the document click on add button.' : ''}`}
                                />
                            )}
                        </div>
                    </div>
                )}
            </div>


            {/* Completion certificate modal */}
            <CompletionCertificateModal
                isOpen={showDetailModal}
                dataState={dataState}
                onClose={handleCloseDetailModal}
                onFetchData={completionCertificateDataGet}
                toast={toast}
                serviceId={serviceId}
                selectedComCerData={selectedComCerData!}
            />


            {/* Document view modal */}
            <DocumentViewModal
                isOpen={showFileViewModal}
                onClose={handleCloseDocumentViewModal}
                fileUrl={fileUrl!}
            />
        </React.Fragment>
    )
}

export default CompletionCertificate;